import { mdiChevronRight, mdiChevronLeft } from '@mdi/js';
import React from 'react';
import { Amount } from '../../api/server/models';
import { formatDate } from '../../services/primitives/date-format';
import { formatNumber } from '../../services/primitives/number-format';
import { truncateUuid } from './util';
import * as S from './claims-bundle-badge.styles';

const formatAmount = formatNumber('Currency');
const formatDateShort = formatDate('Date', 'UTC');

export interface ClaimInfo {
    id: string;
    chargeAmount?: Amount;
    fromDos?: string;
}

export interface IRelatedClaimsBadgeProps {
    className?: string;
    bundleId?: string;
    claims?: ClaimInfo[];
    isExpanded?: boolean;
    onToggle?: () => void;
    claimBundleIds?: string[];
    forceClaims?: boolean;
    onCopy?: (url: string) => void;
    onRemove?: () => void;
}

export const ClaimsBundleBadge: React.FunctionComponent<IRelatedClaimsBadgeProps> = React.memo(
    (props: IRelatedClaimsBadgeProps): React.ReactElement | null => {
        const {
            className,
            bundleId,
            claims,
            isExpanded = false,
            onToggle,
            claimBundleIds,
            forceClaims,
            onCopy,
            onRemove,
        } = props;

        if (claimBundleIds && claimBundleIds.length > 0) {
            return (
                <S.Wrapper className={className}>
                    {claimBundleIds.map(id => (
                        <S.ClaimBadge
                            key={id}
                            forceClaim={forceClaims}
                            claimId={id}
                            onCopy={onCopy}
                            onRemove={onRemove}
                            openInNewTab={true}>
                            Claim ID – {truncateUuid(id)}
                        </S.ClaimBadge>
                    ))}
                </S.Wrapper>
            );
        }

        if (!bundleId || !claims || !onToggle) {
            return null;
        }

        const handleToggle = (e: React.MouseEvent<Element, MouseEvent>) => {
            e.stopPropagation();
            onToggle();
        };

        if (claims.length === 0) {
            return null;
        }

        return (
            <S.ExpandableWrapper className={className} isExpanded={isExpanded}>
                <S.Badge onClick={handleToggle}>
                    <S.BundleText>Bundle: {truncateUuid(bundleId)}</S.BundleText>
                    <S.ActionButton>
                    <S.ChevronIcon
                        path={isExpanded ? mdiChevronLeft : mdiChevronRight }
                        size={0.75}
                        rotated={isExpanded}
                    />
                    </S.ActionButton>
                </S.Badge>
                <S.ClaimsContainer isExpanded={isExpanded}>
                    {claims.map(claim => {
                        const amountText = claim.chargeAmount?.amount
                            ? formatAmount(claim.chargeAmount.amount)
                            : '';
                        const dateText = claim.fromDos ? claim.fromDos : '';
                        const displayText =
                            amountText && dateText
                                ? `${amountText} | ${dateText}`
                                : amountText || dateText || truncateUuid(claim.id);

                        return (
                            <S.ClaimBadgeEnhanced
                                key={claim.id}
                                forceClaim={forceClaims}
                                claimId={claim.id}
                                onCopy={onCopy}
                                onRemove={onRemove}
                                openInNewTab={true}>
                                {displayText}
                            </S.ClaimBadgeEnhanced>
                        );
                    })}
                </S.ClaimsContainer>
            </S.ExpandableWrapper>
        );
    },
);
